// Modal.js
import React from "react";
import styles from "./tnxDetailsModal.module.css"; // Create a CSS module for styling
import { useEnclaveApi } from "../EnclaveSDK/context/EnclaveConnectProvider";
import { ArrowDown, SendHorizontal, Coins } from "lucide-react";
import { getExplorerUrl } from "../EnclaveSDK/EnclaveUtils/functions";
import { formatDate } from "../../utils/functions";
import { networkDetails } from "../../components/EnclaveSDK/EnclaveUtils/constants";
import { getCashToken } from "../../utils/constants";
import { ethers } from "ethers";
import dollarLogo from "../../assets/dollar_jpeg 1.png";
import { formatPrice } from "../../utils/priceUtils";

const replaceUSDC = (str) => str?.replace(/USDC/g, "USD");

const TxnDetailsModal = ({
  isOpen,
  closeTxnDetailsModal,
  txn,
  allTokensList,
  chainDetails,
}) => {
  const { walletAddress } = useEnclaveApi();
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      closeTxnDetailsModal();
    }
  };

  const handleContentClick = (e) => {
    e.stopPropagation(); // Prevent clicks inside modal from closing it
  };

  const spliceAddress = (address) => {
    return ethers.isAddress(address) || address?.length > 12
      ? address.slice(0, 8) + "..." + address.slice(-4)
      : address;
  };

  const metaData = txn.request.metaData;

  const tokenList = [
    getCashToken(networkDetails[chainDetails.id]),
    ...allTokensList,
  ];

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent} onClick={handleContentClick}>
        <div className={styles.tokenLogoBox}>
          <>
            {!metaData ? (
              <div
                className={styles.tokenLogo}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#2D2D2D",
                }}
              >
                <Coins size={24} />
              </div>
            ) : (
              <img
                className={styles.tokenLogo}
                src={
                  metaData.type === "TRANSFER" &&
                  (metaData.tokenSymbol === "USD" ||
                    metaData.tokenSymbol === "USDC")
                    ? dollarLogo
                    : metaData.type === "TRANSFER" || metaData.type === "SELL"
                    ? tokenList.find(
                        (token) => token.symbol === metaData.tokenSymbol
                      )?.logoURI
                    : tokenList.find(
                        (token) => token.symbol === metaData.destinationToken
                      )?.logoURI
                }
                alt={chainDetails.name}
              />
            )}
            {metaData?.type === "BUY" || metaData?.type === "SELL" ? (
              <img
                className={styles.networkLogo}
                src={
                  metaData.type === "SELL"
                    ? tokenList.find(
                        (token) => token.symbol === metaData.destinationToken
                      )?.logoURI
                    : tokenList.find(
                        (token) => token.symbol === metaData.sourceToken
                      )?.logoURI
                }
                style={{
                  width: "28px",
                  height: "28px",
                }}
                alt={chainDetails.name}
              />
            ) : (
              <div className={styles.actionIcon}>
                {metaData?.type === "TRANSFER" ? (
                  <SendHorizontal size={15} color="white" />
                ) : (
                  <ArrowDown size={18} color="white" />
                )}
              </div>
            )}
          </>
        </div>

        <h1 style={{ marginBottom: "20px" }}>
          {!metaData
            ? txn.request.label
            : metaData.type === "BUY" || metaData.type === "SELL"
            ? `Swapped ${replaceUSDC(metaData.sourceToken)} for ${replaceUSDC(
                metaData.destinationToken
              )}`
            : metaData.type === "TRANSFER"
            ? `Sent to ${spliceAddress(metaData.toAccount)}`
            : metaData.label}
        </h1>
        <div className={styles.detailsContainer}>
          <div className={styles.detailRow}>
            <span className={styles.label}>Date</span>
            <span className={styles.value}>
              {formatDate(txn.response.timestamp)}
            </span>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.label}>Network</span>
            <span className={styles.value}>
              {networkDetails[txn.request.network].name}
            </span>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.label}>Transaction Hash</span>
            <span className={styles.value}>
              {spliceAddress(txn.response.txnHash)}
            </span>
          </div>
        </div>
        <p className={styles.detailsHeader}>
          {!metaData
            ? "Transaction Details"
            : metaData.type === "BUY" || metaData.type === "SELL"
            ? "Swap Details"
            : "Transfer Details"}
        </p>
        <div className={styles.detailsContainer}>
          {!metaData ? (
            <div className={styles.detailRow}>
              <span className={styles.label}>Details</span>
              <span className={styles.value}>{txn.request.label}</span>
            </div>
          ) : metaData.type === "BUY" || metaData.type === "SELL" ? (
            <>
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {replaceUSDC(metaData.sourceToken)} Sent
                </span>
                <span className={styles.value}>
                  {"-"}
                  {formatPrice(metaData?.sourceAmount)}{" "}
                  {replaceUSDC(metaData.sourceToken)}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Asset Received</span>
                <span
                  className={styles.value}
                  style={{ color: "#4dd15b", textAlign: "right" }}
                >
                  {"+"}
                  {formatPrice(metaData.destinationAmount)}
                  {" " + replaceUSDC(metaData.destinationToken)}
                </span>
              </div>
            </>
          ) : metaData.type === "TRANSFER" ? (
            <>
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {replaceUSDC(metaData.tokenSymbol)} Sent
                </span>
                <span className={styles.value}>
                  {"-"}
                  {formatPrice(metaData.tokenAmount)}{" "}
                  {replaceUSDC(metaData.tokenSymbol)}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Recipient Address</span>
                <span className={styles.value} style={{ textAlign: "right" }}>
                  {spliceAddress(metaData.toAccount)}
                </span>
              </div>
              {metaData.toUsername && (
                <div className={styles.detailRow}>
                  <span className={styles.label}>Recipient Username</span>
                  <span className={styles.value} style={{ textAlign: "right" }}>
                    {metaData.toUsername}
                  </span>
                </div>
              )}
            </>
          ) : null}

          <div className={styles.viewButtonContainer}>
            <a
              href={`${getExplorerUrl(txn.request.network)}${
                txn.response.txnHash
              }`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.viewButton}
            >
              View on Explorer
            </a>
          </div>
        </div>
        <button
          className={styles.closeButton}
          onClick={(e) => {
            e.stopPropagation();
            closeTxnDetailsModal();
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default TxnDetailsModal;
